import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, useToast } from '@hiring-intelligence/ui';
import { interviewNotesApi } from '@/services/api';
import { Sparkles, RefreshCw } from 'lucide-react';

interface InterviewQuestionsPanelProps {
	sourcedCandidateId: string;
	hiringRequestId: string;
}

export function InterviewQuestionsPanel({ sourcedCandidateId, hiringRequestId }: InterviewQuestionsPanelProps) {
	const { toast } = useToast();
	const queryClient = useQueryClient();
	const [isGenerating, setIsGenerating] = useState(false);

	const { data: questionsData, isLoading } = useQuery({
		queryKey: ['interview-questions', sourcedCandidateId],
		queryFn: () => interviewNotesApi.getQuestions(sourcedCandidateId)
	});

	const generateMutation = useMutation({
		mutationFn: () => {
			setIsGenerating(true);
			return interviewNotesApi.generateQuestions({
				sourcedCandidateId,
				hiringRequestId
			});
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['interview-questions', sourcedCandidateId] });
			toast({ title: 'Questions Generated', description: 'Interview questions are ready.' });
			setIsGenerating(false);
		},
		onError: (err: Error) => {
			toast({ title: 'Generation Failed', description: err.message, variant: 'destructive' });
			setIsGenerating(false);
		}
	});

	const questions = questionsData?.data;

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-8">
				<div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
			</div>
		);
	}

	return (
		<div className="space-y-4 pt-2">
			{!questions ? (
				<div className="text-center py-8 space-y-3">
					<p className="text-sm text-muted-foreground">
						Generate personalized interview questions based on this candidate's experience, education, and certifications matched against the hiring request requirements.
					</p>
					<Button
						onClick={() => generateMutation.mutate()}
						disabled={isGenerating}
					>
						{isGenerating ? (
							<>
								<RefreshCw className="h-4 w-4 mr-2 animate-spin" />
								Generating...
							</>
						) : (
							<>
								<Sparkles className="h-4 w-4 mr-2" />
								Generate Interview Questions
							</>
						)}
					</Button>
				</div>
			) : (
				<div className="space-y-3">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-2">
							<Sparkles className="h-4 w-4 text-primary" />
							<span className="text-sm font-medium">Generated Questions</span>
						</div>
						<Button
							variant="outline"
							size="sm"
							onClick={() => generateMutation.mutate()}
							disabled={isGenerating}
						>
							{isGenerating ? (
								<>
									<RefreshCw className="h-3 w-3 mr-1 animate-spin" />
									Regenerating...
								</>
							) : (
								<>
									<RefreshCw className="h-3 w-3 mr-1" />
									Regenerate
								</>
							)}
						</Button>
					</div>

					<div className="border rounded-md p-4 prose prose-sm dark:prose-invert max-w-none">
						<div
							dangerouslySetInnerHTML={{ __html: renderMarkdown(questions.content) }}
						/>
					</div>

					<div className="text-xs text-muted-foreground text-right">
						Generated {new Date(questions.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
					</div>
				</div>
			)}
		</div>
	);
}

// Simple markdown renderer (headings, bold, italic, lists)
function renderMarkdown(content: string): string {
	if (!content) return '';

	return content
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/^### (.+)$/gm, '<h3>$1</h3>')
		.replace(/^## (.+)$/gm, '<h2>$1</h2>')
		.replace(/^# (.+)$/gm, '<h1>$1</h1>')
		.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
		.replace(/\*(.+?)\*/g, '<em>$1</em>')
		.replace(/^- (.+)$/gm, '<li>$1</li>')
		.replace(/(<li>.*<\/li>)/gs, '<ul>$1</ul>')
		.replace(/<\/ul>\s*<ul>/g, '')
		.replace(/\n\n/g, '<br/><br/>')
		.replace(/\n/g, '<br/>');
}
